import type {
  BaseResponse,
  UploadFileWithMultipartParams,
  UploadPartFileMetaInfoResp,
  UploadPartFileMetaInfoResponse,
} from '@/netadapters/CloudAdapter'
import { INSTANCE as cloudAdapter } from '@/netadapters/CloudAdapter'
import type { ICloudStorage } from '@/cloudstorage/ICloudStorage'
import { FileMultiparts } from '@core/FileUtils'
import type { IFileTransmissionProgressListener } from '@core/FileTransmissionProgressListener'
import {
  DefaultFileTransmissionProgressListener,
  FileTransmissionError,
  MultipartUploadProgressListener,
} from '@core/FileTransmissionProgressListener'
import type { HttpRequestCancelToken } from '@/core/HttpRequestCancelToken'
import { v4 as uuidV4 } from 'uuid'

const PARAM_VALUE_FILE_UPLOAD_BUSINESS_DEVICE = 'deviceFile'
const PARAM_FILE = 'file'

interface FileUploadParameters {
  [key: string]: string | number
  businessId: string
  parentDir: string
  businessType: string
  lastModified: number
  fileName: string
  fileMd5: string
}

enum FileMultipartUploadState {
  INIT = 'init',
  CANCEL = 'cancel',
  COMPLETE = 'complete',
  DELETE = 'delete',
}

class CloudStorage implements ICloudStorage {
  async uploadFileWithMultipart(
    file: File,
    deviceId: string,
    targetPath: string,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<void> {
    let basicParams: FileUploadParameters

    try {
      basicParams = await CloudStorage.#genFileUploadBasicParams(deviceId, targetPath, file, requestCancelToken)
    } catch (e) {
      progressListener?.onError(FileTransmissionError.INIT_FAILED)

      return
    }

    const fileMultiparts = new FileMultiparts(file, undefined, undefined, undefined, undefined, requestCancelToken)
    const multipartInitParams: UploadFileWithMultipartParams = {
      ...basicParams,
      fileSize: file.size,
      partSize: fileMultiparts.sizePerChunk,
      partNumber: fileMultiparts.totalChunks,
    }

    if (requestCancelToken?.isCancelled) return

    const listener = progressListener
      ? new MultipartUploadProgressListener(multipartInitParams.fileSize, progressListener)
      : undefined
    const initData = await cloudAdapter.initFileMultipartUploading(multipartInitParams, requestCancelToken)
    const taskId = initData?.data.multiPartId

    if (!taskId) {
      progressListener?.onError(FileTransmissionError.INIT_FAILED)

      return
    }

    listener?.onStart(taskId)
    const uploadingResult = await this.#startFileMultipartUploading(
      taskId,
      fileMultiparts,
      listener,
      requestCancelToken
    )

    this.#handleUploadingResult(uploadingResult, taskId, listener)
  }

  async resumeUploadingFileWithMultipart(
    multipartId: string,
    file: File,
    transmitted: number,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<void> {
    const listener = progressListener ? new MultipartUploadProgressListener(file.size, progressListener) : undefined

    if (listener) {
      listener.startThreshold = transmitted
    }
    listener?.onStart(multipartId)
    const res = await this.#getFileMultiPartInfoById(multipartId, requestCancelToken)
    const metaInfo = res?.data

    if (metaInfo) {
      switch (metaInfo.fileStatus) {
        case FileMultipartUploadState.INIT:
          if (this.#isComplete(metaInfo)) {
            await this.#completeFileMultipartUploading(multipartId, listener)
          } else {
            const fileMultiparts = new FileMultiparts(
              file,
              metaInfo.partSize,
              metaInfo.partSize * metaInfo.maxPartNumber + 1,
              metaInfo.totalPart - metaInfo.maxPartNumber,
              metaInfo.maxPartNumber
            )

            listener && (listener.transmittedSize = metaInfo.partSize * metaInfo.maxPartNumber)

            const uploadingResult = await this.#startFileMultipartUploading(
              multipartId,
              fileMultiparts,
              listener,
              requestCancelToken
            )

            this.#handleUploadingResult(uploadingResult, multipartId, listener)
          }

          break
        case FileMultipartUploadState.DELETE:
          listener?.onError(FileTransmissionError.RESUME_FAILED)
          break
        // TODO： 后续这里把errortype再加附加信息，辅助传输列表在收到RESUME_FAILED的时候可以决策下一步的操作
        default:
          listener?.onError(FileTransmissionError.UNKNOWN)
          break
      }
    }
  }

  async #handleUploadingResult(
    isSuccess: boolean,
    multipartId: string,
    progressListener?: MultipartUploadProgressListener
  ) {
    if (isSuccess) {
      this.#completeFileMultipartUploading(multipartId, progressListener)
    }
  }

  async uploadFile(
    file: File,
    deviceId: string,
    targetPath: string,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<BaseResponse | undefined> {
    let formData: FormData

    try {
      formData = await this.#assembleFileUploadFormData(deviceId, targetPath, file)
    } catch (e) {
      progressListener?.onError(FileTransmissionError.INIT_FAILED)

      return undefined
    }

    const listener = progressListener ? new DefaultFileTransmissionProgressListener(progressListener) : undefined

    return cloudAdapter.uploadFile(formData, listener, requestCancelToken)
  }

  async downloadFile(
    fileInfo: {
      url: string
      size: number
      name: string
    },
    progressListener: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken,
    isLan?: boolean,
    isSupportRange?: boolean
  ): Promise<string | undefined> {
    return cloudAdapter.downloadFile(fileInfo, progressListener, requestCancelToken, isLan, isSupportRange)
  }

  /**
   * 分片上传完成
   * @param multipartId
   * @returns
   */
  async getFileMultipartUploadByMultipartId(multipartId: string) {
    await cloudAdapter.completeFileMultipartUploading(multipartId)
  }

  /**
   * 按指定的分片上传标识查询上传信息
   * @param multipartId
   * @returns
   */
  async #getFileMultiPartInfoById(
    multipartId: string,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<UploadPartFileMetaInfoResponse | undefined> {
    return cloudAdapter.getFileMultiPartInfoById(multipartId, requestCancelToken)
  }

  /**
   * 取消上传, 和multipart id相关的所有分片将从服务器移除
   *
   * @param multipartId
   * @returns
   */
  async cancelFileMultipartUploadingById(multipartId: string): Promise<boolean> {
    const res = cloudAdapter.cancelFileMultipartUploadingById(multipartId)

    return res != undefined
  }

  async #startFileMultipartUploading(
    multipartId: string,
    fileMultiparts: FileMultiparts,
    progressListener?: MultipartUploadProgressListener | undefined,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<boolean> {
    for (const part of fileMultiparts) {
      if (requestCancelToken?.isCancelled) return false

      if (progressListener) {
        progressListener.currentChunkSize = part.size
      }

      try {
        const res = await cloudAdapter.uploadFileMultipartById(
          multipartId,
          part.chunk,
          part.index + 1,
          progressListener,
          requestCancelToken,
          part.name
        )

        if (!res) {
          progressListener?.onError(FileTransmissionError.MULTIPART_UPLOADING_FAILED)

          return false
        }
      } catch (error) {
        progressListener?.onError(FileTransmissionError.MULTIPART_UPLOADING_FAILED)

        return false
      }
    }

    return true
  }

  static async #genFileUploadBasicParams(
    deviceId: string,
    targetPath: string,
    file: File,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<FileUploadParameters> {
    const params: FileUploadParameters = {
      businessId: deviceId,
      businessType: PARAM_VALUE_FILE_UPLOAD_BUSINESS_DEVICE,
      fileMd5: '',
      fileName: file.name,
      lastModified: file.lastModified,
      parentDir: targetPath,
    }

    params.fileMd5 = uuidV4()

    return params
  }

  /**
   * uploadFile参数组装
   * @param deviceId 设备ID
   * @param targetPath 手机端地址
   * @param file 文件
   */
  async #assembleFileUploadFormData(deviceId: string, targetPath: string, file: File): Promise<FormData> {
    const params = await CloudStorage.#genFileUploadBasicParams(deviceId, targetPath, file)
    const formData = new FormData()

    for (const key of Object.keys(params)) {
      formData.append(key, params[key].toString())
    }

    formData.append(PARAM_FILE, file)

    return formData
  }

  #isComplete(metaInfo: UploadPartFileMetaInfoResp): boolean {
    return metaInfo.totalPart === metaInfo.maxPartNumber
  }

  async #completeFileMultipartUploading(multipartId: string, progressListener?: MultipartUploadProgressListener) {
    const res = await cloudAdapter.completeFileMultipartUploading(multipartId)
    const fileId = res?.data

    if (fileId) {
      progressListener?.onCompleted(fileId)
    } else {
      progressListener?.onError(FileTransmissionError.COMPLETE_REQUEST_FAILED)
    }
  }
}

const INSTANCE = new CloudStorage()

export default {
  INSTANCE,
}
